import { createContext, ReactNode, useCallback, useEffect, useMemo, useReducer } from 'react';
import { KEY_JWT, KEY_REFRESH_TOKEN } from 'src/utils/utilsLocalStorage';

// ----------------------------------------------------------------------

type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

type AuthUser = null | Record<string, any>;
type TwoFactorLoginPurpose = 'init' | 'setup' | 'login' | 'done';

type AuthState = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
  jwt: string;
  refreshToken: string;
  loginState: {
    preAuthToken: string;
    twoFactorPurpose: TwoFactorLoginPurpose;
  };
  numOfRecoveryCodesLeft: number;
  recoveryCodes: string[];
  isRefreshingToken: boolean;
};

type FnLogin = (
  username: string,
  password: string
) => Promise<{
  preAuthToken: string;
  purpose: TwoFactorLoginPurpose;
}>;

type FnLoginTwoFactor = (code: string) => Promise<{
  token: string;
  refreshToken: string;
}>;

type FnLoginRecovery = (code: string) => Promise<{
  token: string;
  refreshToken: string;
  remaining: number;
}>;

type FnTwoFactorSetupInit = (preAuthToken: string) => Promise<{
  otpauthUrl: string;
}>;

type FnTwoFactorSetupConfirm = (
  preAuthToken: string,
  code: string
) => Promise<{
  token: string;
  refreshToken: string;
  recoveryCodes: string[];
}>;

type AuthContextType = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
  jwt: string;
  recoveryCodes: string[];
  isRefreshingToken: boolean;
  login: FnLogin;
  tokenRefresh: () => Promise<void>;
  logout: VoidFunction;
  setJwt: (jwt: string) => Promise<void>;
  setRefreshToken: (refreshToken: string) => Promise<void>;

  loginTwoFactor: FnLoginTwoFactor;
  loginRecovery: FnLoginRecovery;
  twoFactorSetupInit: FnTwoFactorSetupInit;
  twoFactorSetupConfirm: FnTwoFactorSetupConfirm;
  clearRecoveryCodes: VoidFunction;
  removeTokensFromStorage: VoidFunction;
};

// ----------------------------------------------------------------------

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  jwt: '',
  refreshToken: '',
  loginState: {
    preAuthToken: '',
    twoFactorPurpose: 'init',
  },
  numOfRecoveryCodesLeft: 100, // magic number, backend is set to 8, keep for future flexibility
  recoveryCodes: [],
  isRefreshingToken: false,
};

enum Types {
  init = 'INITIALIZE',
  login = 'LOGIN',
  tokenRefresh = 'TOKEN_REFRESH',
  refreshingToken = 'REFRESHING_TOKEN',
  logout = 'LOGOUT',
  setJwt = 'SET_JWT',
  setRefreshToken = 'SET_REFRESH_TOKEN',
  setLoginState = 'SET_LOGIN_STATE',
  setNumOfRecoveryCodesLeft = 'SET_NUM_OF_RECOVERY_CODES_LEFT',
  setRecoveryCodes = 'SET_RECOVERY_CODES',
}

type Auth0AuthPayload = {
  [Types.init]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.login]: {
    user: AuthUser;
  };
  [Types.tokenRefresh]: undefined;
  [Types.refreshingToken]: boolean;
  [Types.logout]: undefined;
  [Types.setJwt]: {
    jwt: string;
  };
  [Types.setRefreshToken]: {
    refreshToken: string;
  };
  [Types.setLoginState]: {
    preAuthToken: string;
    twoFactorPurpose: TwoFactorLoginPurpose;
  };
  [Types.setNumOfRecoveryCodesLeft]: {
    numOfRecoveryCodesLeft: number;
  };
  [Types.setRecoveryCodes]: {
    recoveryCodes: string[];
  };
};

type Auth0Actions = ActionMap<Auth0AuthPayload>[keyof ActionMap<Auth0AuthPayload>];

const reducer = (state: AuthState, action: Auth0Actions): AuthState => {
  if (action.type === Types.init) {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }
  if (action.type === Types.login) {
    const { user } = action.payload;
    return { ...state, isAuthenticated: true, user };
  }
  if (action.type === Types.tokenRefresh) {
    return { ...state };
  }
  if (action.type === Types.refreshingToken) {
    return { ...state, isRefreshingToken: action.payload };
  }
  if (action.type === Types.logout) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === Types.setJwt) {
    return {
      ...state,
      jwt: action.payload.jwt,
    };
  }
  if (action.type === Types.setLoginState) {
    const { preAuthToken, twoFactorPurpose } = action.payload;
    return {
      ...state,
      loginState: {
        preAuthToken,
        twoFactorPurpose,
      },
    };
  }
  if (action.type === Types.setNumOfRecoveryCodesLeft) {
    const { numOfRecoveryCodesLeft } = action.payload;
    return {
      ...state,
      numOfRecoveryCodesLeft,
    };
  }
  if (action.type === Types.setRecoveryCodes) {
    const { recoveryCodes } = action.payload;
    return {
      ...state,
      recoveryCodes,
    };
  }
  return state;
};

const AuthContext = createContext<AuthContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

const REST_API_ROUTE = import.meta.env.VITE_REACT_APP_API;

const URL_LOGIN__INIT = REST_API_ROUTE + '/login';
const URL_LOGIN__2FA_CONFIRM = REST_API_ROUTE + '/login/2fa-confirm';
const URL_LOGIN__2FA_RECOVERY = REST_API_ROUTE + '/login/recovery';

const URL_REFRESH_TOKEN = REST_API_ROUTE + '/refreshtoken';
const URL_2FA_SETUP__INIT = REST_API_ROUTE + '/2fa/setup';
const URL_2FA_SETUP__CONFIRM = REST_API_ROUTE + '/2fa/confirm';

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setJwt = useCallback(async (jwt: any) => {
    localStorage.setItem(KEY_JWT, jwt);
    dispatch({ type: Types.setJwt, payload: { jwt: jwt } });
  }, []);

  const setRefreshToken = useCallback(async (refreshToken: string) => {
    localStorage.setItem(KEY_REFRESH_TOKEN, refreshToken);
    dispatch({ type: Types.setRefreshToken, payload: { refreshToken } });
  }, []);

  const removeTokensFromStorage = useCallback(() => {
    localStorage.removeItem(KEY_JWT);
    localStorage.removeItem(KEY_REFRESH_TOKEN);
  }, []);

  const updateStateUponLoginSuccess = useCallback(
    (data: { token: string; refreshToken: string }) => {
      setJwt(data.token);
      setRefreshToken(data.refreshToken);
      dispatch({ type: Types.login, payload: { user: data || null } });
    },
    [setJwt, setRefreshToken]
  );

  const login: FnLogin = useCallback(async (username, password) => {
    try {
      const response = await fetch(URL_LOGIN__INIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || response.statusText);
      }

      const data = await response.json();
      dispatch({ type: Types.setLoginState, payload: { ...data } });
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, []);

  /**
   * @description Function to handle submission of a 2FA code for login.
   * This is used after the initial username/password login step if the user has 2FA enabled.
   * If successful, the user will be granted access to the app.
   */
  const loginTwoFactor: FnLoginTwoFactor = useCallback(
    async (code: string) => {
      try {
        const response = await fetch(URL_LOGIN__2FA_CONFIRM, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preAuthToken: state.loginState.preAuthToken,
            secret: code,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || response.statusText);
        }

        const tokens = await response.json();

        updateStateUponLoginSuccess(tokens);

        return tokens;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [state.loginState.preAuthToken, updateStateUponLoginSuccess]
  );

  /**
   * @description  Function to handle submission of a 2FA recovery code for login.
   * This is used if the user has lost access to their 2FA device and needs to use a recovery code to log in.
   * If successful, the user will be granted access to the app.
   */
  const loginRecovery: FnLoginRecovery = useCallback(
    async (code: string) => {
      try {
        const response = await fetch(URL_LOGIN__2FA_RECOVERY, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preAuthToken: state.loginState.preAuthToken,
            recoveryCode: code,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || response.statusText);
        }

        const tokens = await response.json();

        if (tokens.remaining > 0) {
          dispatch({
            type: Types.setNumOfRecoveryCodesLeft,
            payload: { numOfRecoveryCodesLeft: tokens.remaining },
          });
        }

        updateStateUponLoginSuccess(tokens);
        return tokens;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [state.loginState.preAuthToken, updateStateUponLoginSuccess]
  );

  /**
   * @description Function to initialize the 2FA setup process.
   * This is called by user interaction when a user opts to set up 2FA on their account OR
   * called programmatically during login if the user is found to that the 2FA is not setup yet.
   */
  const twoFactorSetupInit = useCallback(async (preAuthToken: string) => {
    try {
      const response = await fetch(URL_2FA_SETUP__INIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preAuthToken: preAuthToken,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || response.statusText);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, []);

  /**
   * @description Function to handle submission of the **FIRST** 2FA code during 2FA setup, in order to finalize the setup process.
   */
  const twoFactorSetupConfirm: FnTwoFactorSetupConfirm = useCallback(
    async (preAuthToken, code) => {
      try {
        const response = await fetch(URL_2FA_SETUP__CONFIRM, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preAuthToken: preAuthToken,
            secret: code,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || response.statusText);
        }

        const data = await response.json();

        // Store the recovery codes in the state so that the `DialogTwoFactorRecoveryCodeDownload` component
        // can prompt the user to download them
        dispatch({
          type: Types.setRecoveryCodes,
          payload: { recoveryCodes: data.recoveryCodes },
        });

        updateStateUponLoginSuccess(data);
        return data;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [updateStateUponLoginSuccess]
  );

  /**
   * @description Function to clear the 2FA recovery codes from the state,
   * typically called after the user has downloaded their recovery codes
   * or if they choose to dismiss the download prompt, so that
   * the `DialogTwoFactorRecoveryCodeDownload` component could be dismissed
   */
  const clearRecoveryCodes: VoidFunction = useCallback(() => {
    dispatch({
      type: Types.setRecoveryCodes,
      payload: { recoveryCodes: [] },
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY_JWT);
    localStorage.removeItem(KEY_REFRESH_TOKEN);
    window.location.href = '/sign-in';
    dispatch({ type: Types.logout });
  }, []);

  const tokenRefresh = useCallback(async () => {
    dispatch({ type: Types.refreshingToken, payload: true });
    try {
      const refreshToken = localStorage.getItem(KEY_REFRESH_TOKEN);
      const response = await fetch(URL_REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const tokens = await response.json();

      setJwt(tokens.token);
      setRefreshToken(tokens.refreshToken);
      dispatch({ type: Types.refreshingToken, payload: false });
    } catch (error) {
      dispatch({ type: Types.refreshingToken, payload: false });
      console.error(error);
      logout();
      throw error;
    }
  }, [setJwt, setRefreshToken, logout]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const jwtLocalStorage = localStorage.getItem(KEY_JWT);

        if (jwtLocalStorage) {
          var jwt = jwtLocalStorage;
          setJwt(jwt!);
          dispatch({
            type: Types.init,
            payload: { isAuthenticated: true, user: null },
          });
        } else {
          dispatch({
            type: Types.init,
            payload: { isAuthenticated: false, user: null },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.init,
          payload: { isAuthenticated: false, user: null },
        });
      }
    };

    initialize();
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      jwt: state?.jwt,
      login,
      tokenRefresh,
      logout,
      setJwt,
      setRefreshToken,

      loginTwoFactor,
      loginRecovery,
      twoFactorSetupInit,
      twoFactorSetupConfirm,
      clearRecoveryCodes,
      removeTokensFromStorage,
    }),
    [
      state,
      login,
      tokenRefresh,
      logout,
      setJwt,
      setRefreshToken,

      loginTwoFactor,
      loginRecovery,
      twoFactorSetupInit,
      twoFactorSetupConfirm,
      clearRecoveryCodes,
      removeTokensFromStorage,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
